/* eslint-disable react/jsx-props-no-spreading */
import { useUser } from '@folklore/auth';
import classNames from 'classnames';
import { default as isAfter } from 'date-fns/isAfter';
import { default as isBefore } from 'date-fns/isBefore';
import { default as parseISO } from 'date-fns/parseISO';
import { camelCase } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useLabels } from '../../../contexts/LabelsContext';
import { useThemeClassName } from '../../../contexts/ThemeContext';
import Button from '../../buttons/Button';
import EnterPinCodeForm from '../../forms/EnterPincodeForm';
import ExplanationModal from '../../modals/ExplanationModal';
import GameItem from './Default';

import styles from '../../../../styles/items/games/bonus.module.css';

interface BonusGameItemProps {
    game: Game;
    canParticipate?: boolean;
    canParticipateAt?: string | null;
    className?: string | null;
    cardClassName?: string | null;
}

function BonusGameItem({
    game,
    canParticipate = true,
    canParticipateAt = null,
    className = null,
}: BonusGameItemProps) {
    const themeClassName = useThemeClassName(styles);
    const {
        type,
        url,
        startAt = null,
        endAt = null,
        subtitle = null,
        explanationModal = null,
        pinModal = null,
    } = game || {};

    const user = useUser();
    const { unusedPincode = null, lastPincode = null } = user || {};
    const { code = null } = unusedPincode || {};

    const { bonusItemPinExplaination = null } = useLabels();

    const itemClassName = subtitle !== null ? camelCase(subtitle) : null;

    const [modalOpen, setModalOpen] = useState(false);
    const openModal = useCallback(() => setModalOpen(true), [setModalOpen]);
    const closeModal = useCallback(() => {
        setModalOpen(false);
    }, [setModalOpen]);

    const startAtDate = useMemo(() => (startAt !== null ? parseISO(startAt) : null), [startAt]);
    const endAtDate = useMemo(() => (endAt !== null ? parseISO(endAt) : null), [endAt]);
    const canParticipateAtDate = useMemo(
        () => (canParticipateAt !== null ? parseISO(canParticipateAt) : null),
        [canParticipateAt],
    );

    const isStarted = useMemo(
        () => startAtDate === null || isBefore(startAtDate, new Date()),
        [startAtDate],
    );

    const isEnded = useMemo(
        () => endAtDate !== null && isBefore(endAtDate, new Date()),
        [endAtDate],
    );

    const inactive = useMemo(
        () => !isStarted || isEnded || !canParticipate,
        [isStarted, isEnded, canParticipate],
    );

    const showTimer = useMemo(
        () => canParticipateAtDate !== null && isAfter(canParticipateAtDate, new Date()),
        [canParticipateAtDate],
    );

    const cannotPlay = showTimer || !canParticipate;

    return (
        <div
            className={classNames([
                styles.container,
                styles[type],
                {
                    [styles.inactive]: inactive,
                    [itemClassName]: itemClassName !== null,
                },
                themeClassName,
                className,
                'bonus-game-item',
            ])}
        >
            <div className={styles.outer}>
                <h4 className={styles.title}>
                    <FormattedMessage
                        defaultMessage="Unlock the bonus game"
                        description="Bonus label"
                    />
                </h4>
                <div className={styles.inner}>
                    <div
                        className={classNames([
                            styles.bonusGameInner,
                            {
                                [styles.disabled]: code !== null || cannotPlay,
                            },
                        ])}
                    >
                        <div className={styles.bonusGameTop}>
                            <p className={styles.bonusDescription}>
                                {bonusItemPinExplaination || (
                                    <FormattedMessage
                                        defaultMessage="Find the PIN number!"
                                        description="Bonus label"
                                    />
                                )}
                            </p>
                            <Button onClick={openModal} className={styles.smallButton}>
                                <FormattedMessage
                                    defaultMessage="Where can I find my pin?"
                                    description="Button label"
                                />
                            </Button>
                        </div>
                        <EnterPinCodeForm
                            className={styles.bonusForm}
                            value={{ code }}
                            submitLabel={
                                <FormattedMessage
                                    defaultMessage="Confirm"
                                    description="Button label"
                                />
                            }
                            withoutTitle
                        />
                        <h4 className={styles.subtitle}>
                            <FormattedMessage
                                defaultMessage="Bonus game"
                                description="Bonus label"
                            />
                        </h4>
                    </div>
                    <GameItem
                        className={classNames([
                            styles.bonusGame,
                            {
                                [styles.disabled]: code === null || cannotPlay,
                            },
                        ])}
                        game={game}
                    />
                </div>
            </div>
            {modalOpen ? (
                <ExplanationModal
                    blocks={pinModal}
                    url={isStarted && !isEnded ? url : null}
                    onClose={closeModal}
                    withoutButtons
                />
            ) : null}
        </div>
    );
}

export default BonusGameItem;
